import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { COMPONENT_TYPES } from '../../constants';
import {
  removeComponent,
  selectSelectedComponent
} from '../../redux/editorSlice';
import LayoutSelector from '../LayoutSelector';
import RichEditorModal from '../common/RichEditorModal';
import { ComponentPropertiesMap } from './ComponentProperties';
import './PropertyPanel.css';

// 유틸리티와 컨텍스트 import 추가
import { usePropertyPanel, PropertyPanelProvider } from './PropertyPanelContext';
import { PaddingControl, BorderRadiusControl, StyleControl } from './CommonProperties';

const PropertyPanel = () => {
  const dispatch = useDispatch();
  const selectedComponent = useSelector(selectSelectedComponent);
  
  // PropertyPanelContext에서 가져오는 값들
  const { 
    activeTab, 
    setActiveTab,
    devicePropertyTab,
    handleTabChange,
    handleDevicePropertyTabChange,
    isRichEditorOpen,
    richEditorContent,
    handleRichEditorSave,
    setIsRichEditorOpen
  } = usePropertyPanel();
  
  // 컴포넌트 선택 상태에 따라 활성 탭 자동 변경
  useEffect(() => {
    if (selectedComponent) {
      setActiveTab('component');
    } else {
      setActiveTab('layout');
    }
  }, [selectedComponent, setActiveTab]);
  
  // 삭제 핸들러
  const handleDelete = () => {
    dispatch(removeComponent(selectedComponent.id));
  };

  // 공통 속성 컴포넌트 렌더링
  const renderCommonProperties = () => {
    if (!selectedComponent) return null;
    
    return (
      <>
        <PaddingControl />
        <BorderRadiusControl />
        <StyleControl />
      </>
    );
  };

  // 컴포넌트 속성 렌더링 함수
  const renderProperties = () => {
    if (!selectedComponent) return null;
    
    switch(selectedComponent.type) {
      case COMPONENT_TYPES.FIXED_BG_TEXT:
        // 모듈화된 컴포넌트 사용 (이제 Context를 통해 속성을 전달)
        return (
          <>
            <ComponentPropertiesMap.FIXED_BG_TEXT />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.IMAGE_TEXT:
        return (
          <>
            <ComponentPropertiesMap.IMAGE_TEXT />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.SIGNUP:
        return (
          <>
            <ComponentPropertiesMap.SIGNUP />
            {renderCommonProperties()}
          </>
        );
      
      case COMPONENT_TYPES.USER_PROFILE:
        return (
          <>
            <ComponentPropertiesMap.USER_PROFILE />
            {renderCommonProperties()}
          </>
        );
    
      case COMPONENT_TYPES.LOGIN:
        return (
          <>
            <ComponentPropertiesMap.LOGIN />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.BOARD:
        return (
          <>
            <ComponentPropertiesMap.BOARD />
            {renderCommonProperties()}
          </>
        );
      
      case COMPONENT_TYPES.MAIN_BANNER:
        return (
          <>
            <ComponentPropertiesMap.MAIN_BANNER />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.PRODUCT_LIST:
        return (
          <>
            <ComponentPropertiesMap.PRODUCT_LIST />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.PRODUCT_DETAIL:
        return (
          <>
            <ComponentPropertiesMap.PRODUCT_DETAIL />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.FAQ:
        return (
          <>
            <ComponentPropertiesMap.FAQ />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.COMPANY_HISTORY:
        return (
          <>
            <ComponentPropertiesMap.COMPANY_HISTORY />
            {renderCommonProperties()}
          </>
        );

      case COMPONENT_TYPES.ASSET_HISTORY:
        return (
          <>
            <ComponentPropertiesMap.ASSET_HISTORY />
            {renderCommonProperties()}  
          </>
        );

      case COMPONENT_TYPES.MEMBERSHIP_SIGNUP:
        return (
          <>
            <ComponentPropertiesMap.MEMBERSHIP_SIGNUP />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.CUSTOM_MEMBERSHIP_SIGNUP:
        return (
          <>
            <ComponentPropertiesMap.CUSTOM_MEMBERSHIP_SIGNUP />
            {renderCommonProperties()}
          </>
        );

      case COMPONENT_TYPES.RECOMMENDED_USERS:
        return (
          <>
            <ComponentPropertiesMap.RECOMMENDED_USERS />
            {renderCommonProperties()}
          </>
        );

      case COMPONENT_TYPES.BINARY_LEGS:
        return (
          <>
            <ComponentPropertiesMap.BINARY_LEGS />
            {renderCommonProperties()}
          </>
        );  

      case COMPONENT_TYPES.UNILEVEL_LEGS:
        return (
          <>
            <ComponentPropertiesMap.UNILEVEL_LEGS />
            {renderCommonProperties()}
          </>
        );

      case COMPONENT_TYPES.PRODUCT_SLIDER:
        return (
          <>
            <ComponentPropertiesMap.PRODUCT_SLIDER />
            {renderCommonProperties()} 
          </>
        );

      case COMPONENT_TYPES.IMAGE_SLIDER:
        return (
          <>
            <ComponentPropertiesMap.IMAGE_SLIDER />
            {renderCommonProperties()} 
          </>
        );
      
      case COMPONENT_TYPES.TRENDING_ITEMS:
        return (
          <>
            <ComponentPropertiesMap.TRENDING_ITEMS />
            {renderCommonProperties()}
          </>
        );

      case COMPONENT_TYPES.CART:
        return (
          <>
            <ComponentPropertiesMap.CART />
            {renderCommonProperties()}
          </>
        );

      case COMPONENT_TYPES.PAYMENT:
        return (
          <>
            <ComponentPropertiesMap.PAYMENT />
            {renderCommonProperties()}
          </>
        );

      case COMPONENT_TYPES.ORDER_HISTORY:
        return (
          <>
            <ComponentPropertiesMap.ORDER_HISTORY />
            {renderCommonProperties()}
          </>
        );

      case COMPONENT_TYPES.PARALLAX:
        return (
          <>
            <ComponentPropertiesMap.PARALLAX />
            {renderCommonProperties()}
          </>
        );

      case COMPONENT_TYPES.RICH_EDITOR:
        return (
          <>
            <ComponentPropertiesMap.RICH_EDITOR />
            {renderCommonProperties()}
          </>
        );

      case COMPONENT_TYPES.BENEFIT_SUMMARY:
        return (
          <>
            <ComponentPropertiesMap.BENEFIT_SUMMARY />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.MOBILE_LAYOUT:
        return (
          <>
            <ComponentPropertiesMap.MOBILE_LAYOUT />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.CATEGORY_MENU:
        return (
          <>
            <ComponentPropertiesMap.CATEGORY_MENU />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.QUICK_MENU:
        return (
          <>
            <ComponentPropertiesMap.QUICK_MENU />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.DUAL_BANNER:
        return (
          <>
            <ComponentPropertiesMap.DUAL_BANNER />
            {renderCommonProperties()}
          </>
        );
        
      case COMPONENT_TYPES.SEARCH_BAR:
        return (
          <>
            <ComponentPropertiesMap.SEARCH_BAR />
            {renderCommonProperties()}
          </>
        );
        
      // 다른 컴포넌트 타입들도 추가해야 함
      default:
        return (
          <div className="not-implemented">
            <p>해당 컴포넌트 타입({selectedComponent.type})의 속성 편집기가 아직 구현되지 않았습니다.</p>
          </div>
        );
    }
  };

  // 탭 UI 렌더링
  const renderTabs = () => (
    <div className="panel-tabs">
      <button 
        className={`panel-tab ${activeTab === 'layout' ? 'active' : ''}`}
        onClick={() => handleTabChange('layout')}
      >
        레이아웃
      </button>
      <button 
        className={`panel-tab ${activeTab === 'component' ? 'active' : ''}`}
        onClick={() => handleTabChange('component')}
      >
        컴포넌트
      </button>
    </div>
  );
  
  // 디바이스별 속성 탭 UI
  const renderDevicePropertyTabs = () => (
    <div className="device-property-tabs">
      <button 
        className={`device-property-tab ${devicePropertyTab === 'pc' ? 'active' : ''}`}
        onClick={() => handleDevicePropertyTabChange('pc')}
      >
        PC
      </button>
      <button 
        className={`device-property-tab ${devicePropertyTab === 'mobile' || devicePropertyTab === 'tablet' ? 'active' : ''}`}
        onClick={() => handleDevicePropertyTabChange('mobile')}
      >
        모바일/태블릿
      </button>
    </div>
  );
  
  return (
    <div className="property-panel">
      {renderTabs()}
      
      {activeTab === 'layout' ? (
        <div className="layout-options">
          <LayoutSelector />
        </div>
      ) : (
        <div className="component-properties">
          <h3>컴포넌트 속성</h3>
          {renderDevicePropertyTabs()}
          {renderProperties()}
          <button 
            className="delete-button" 
            onClick={handleDelete}
          >
            컴포넌트 삭제
          </button>
        </div>
      )}
      
      {/* 리치 에디터 모달 */}
      {isRichEditorOpen && (
        <RichEditorModal 
          isOpen={isRichEditorOpen}
          initialContent={richEditorContent}
          onSave={(content) => handleRichEditorSave(selectedComponent, content)}
          onClose={() => setIsRichEditorOpen(false)}
        />
      )}
    </div>
  );
};

// PropertyPanelProvider로 감싸서 내보내기
const PropertyPanelWithContext = () => (
  <PropertyPanelProvider>
    <PropertyPanel />
  </PropertyPanelProvider>
);

export default PropertyPanelWithContext;
